import { ApiError } from "@/lib/api";

/**
 * 서버(zod)와 클라이언트(zod) 오류를 한 모양으로 , 어느 칸의 무슨 문제인지.
 * zod 기본 메시지는 영어라, 한글 메시지가 없으면 코드로 옮겨 적는다.
 */
export interface FieldIssue {
  path: PropertyKey[];
  message: string;
  code?: string;
}

const KO_BY_CODE: Record<string, string> = {
  invalid_type: "형식이 올바르지 않습니다",
  too_small: "값이 너무 작거나 짧습니다",
  too_big: "값이 너무 크거나 깁니다",
  invalid_format: "형식이 올바르지 않습니다",
  invalid_value: "허용되지 않는 값입니다",
  invalid_union: "허용되지 않는 형태입니다",
  unrecognized_keys: "알 수 없는 항목이 있습니다",
  not_multiple_of: "허용되지 않는 값입니다",
  custom: "입력값을 확인해 주세요",
};

function koMessage(message: string, code?: string): string {
  if (/[가-힣]/.test(message)) return message;
  return (code && KO_BY_CODE[code]) ?? message;
}

/** 알 수 없는 issues 값(서버 응답)을 안전하게 읽는다 */
export function toIssues(raw: unknown): FieldIssue[] {
  if (!Array.isArray(raw)) return [];
  const out: FieldIssue[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object" || !("message" in item)) continue;
    const i = item as { path?: unknown; message: unknown; code?: unknown };
    const code = typeof i.code === "string" ? i.code : undefined;
    out.push({ path: Array.isArray(i.path) ? (i.path as PropertyKey[]) : [], message: koMessage(String(i.message), code), code });
  }
  return out;
}

/** 첫 경로 조각(필드 이름) → 메시지. 같은 칸에 여러 문제가 있으면 첫 것만 */
export function issuesByField(issues: FieldIssue[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const i of issues) {
    const field = i.path[0];
    if (field === undefined) continue;
    const k = String(field);
    if (!(k in map)) map[k] = i.message;
  }
  return map;
}

export function pathLabel(path: PropertyKey[], labels?: Record<string, string>): string {
  if (path.length === 0) return "전체";
  const head = String(path[0]);
  const rest = path.slice(1).map(String);
  const first = labels?.[head] ?? head;
  return rest.length ? `${first}.${rest.join(".")}` : first;
}

export function errorMessage(e: unknown, fallback = "요청에 실패했습니다. 잠시 뒤 다시 시도해 주세요."): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}
